import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 10000,
});

// Request interceptor to attach CSRF token
api.interceptors.request.use(async (config) => {
  if (['post', 'put', 'delete', 'patch'].includes(config.method)) {
    // CSRF token lives in a non-httpOnly cookie set by the server; read it directly
    // so we're never out of sync with the cookie (e.g. after server-side rotation).
    let token = Cookies.get('csrfToken');
    if (!token) {
      try {
        const response = await axios.get(`${API_BASE_URL}/csrf-token`, { withCredentials: true });
        token = response.data.csrfToken;
      } catch {
        // CSRF token fetch failed — requests may fail with 403
      }
    }
    if (token) {
      config.headers['X-CSRF-Token'] = token;
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    // If CSRF token was rejected, refresh it and retry once
    if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
      const response = await axios.get(`${API_BASE_URL}/csrf-token`, { withCredentials: true });
      const token = response.data.csrfToken;
      if (token) {
        error.config.headers['X-CSRF-Token'] = token;
        const retryResponse = await axios(error.config);
        return retryResponse.data;
      }
    }

    if (error.response) {
      // Server responded with error
      const message = error.response.data?.message || error.response.data?.error || 'An error occurred';

      // Handle 401 - redirect to login (but not if already on login/auth pages)
      if (error.response.status === 401) {
        const path = window.location.pathname;
        if (path.startsWith('/admin') && !path.startsWith('/admin/login') && !path.startsWith('/admin/forgot-password')) {
          window.location.href = '/admin/login';
        } else if (path.startsWith('/member') && !path.startsWith('/member/login') && !path.startsWith('/member/signup') && !path.startsWith('/member/verify-email') && !path.startsWith('/member/forgot-password') && !path.startsWith('/member/reset-password')) {
          window.location.href = '/member/login';
        }
      }

      // Keep the status and payload on the error: some callers need to tell
      // "not found" from "members only" and act differently.
      const apiError = new Error(message);
      apiError.status = error.response.status;
      apiError.data = error.response.data;
      apiError.response = error.response;
      throw apiError;
    } else if (error.request) {
      // Network error
      throw new Error('Network error. Please check your connection.');
    } else {
      throw error;
    }
  }
);

// API methods
export const apiService = {
  // Health
  health: () => api.get('/health'),
  
  // Settings (public)
  getSettings: () => api.get('/settings'),

  // Redirects (public)
  checkRedirect: (path) => api.get('/redirects/check', { params: { path } }),
  
  // Sermons (public)
  getSermons: (params) => api.get('/sermons', { params }),
  getSermon: (slug) => api.get(`/sermons/${slug}`),
  getSermonTranscription: (slug) => api.get(`/sermons/${slug}/transcription`),
  getLatestSermon: (count = 1) => api.get('/sermons/latest', { params: { count } }),
  getCurrentSeries: () => api.get('/sermons/series/current'),
  getAllSeries: () => api.get('/sermons/series'),
  getSpeakers: () => api.get('/sermons/speakers'),
  
  // Announcements (public)
  getAnnouncements: (params) => api.get('/announcements', { params }),
  getFeaturedAnnouncements: (limit = 3) => api.get('/announcements/featured', { params: { limit } }),
  getUrgentAnnouncements: () => api.get('/announcements/urgent'),
  getAnnouncement: (slug) => api.get(`/announcements/${slug}`),
  
  // Events (public)
  getEvents: (params) => api.get('/events', { params }),
  getEvent: (slug) => api.get(`/events/${slug}`),
  registerForEvent: (eventId, data) => api.post(`/events/${eventId}/register`, data),
  lookupRegistration: (code) => api.get(`/events/registration/${code}`),
  cancelRegistration: (code) => api.delete(`/events/registration/${code}`),
  
  // Small Groups (public)
  getSmallGroups: (params) => api.get('/small-groups', { params }),

  // Leadership (public)
  getLeadership: () => api.get('/leadership'),
  
  // Contact (public)
  submitContact: (data) => api.post('/contact', data),
  
  // Catechism (public)
  getCatechism: (params) => api.get('/catechism', { params }),
  getWeeklyCatechism: (type) => api.get('/catechism/weekly', { params: type ? { type } : {} }),
  getWeeklyFeaturedCatechism: () => api.get('/catechism/weekly/featured'),
  getRandomCatechism: (type) => api.get('/catechism/random', { params: type ? { type } : {} }),
  getCatechismCounts: () => api.get('/catechism/counts'),
  getCatechismByType: (type, params) => api.get(`/catechism/type/${type}`, { params }),
  getCatechismQuestion: (type, number) => api.get(`/catechism/type/${type}/${number}`),
  searchCatechism: (q, type) => api.get('/catechism/search', { params: { q, ...(type && { type }) } }),

  // Homepage (public)
  getHomepage: () => api.get('/homepage'),
  getHeroSlides: () => api.get('/homepage/hero-slides'),
  getServiceTimes: () => api.get('/homepage/service-times'),
  getHomeLocation: () => api.get('/homepage/location'),
  getConnectCards: () => api.get('/homepage/connect-cards'),

  // Navigation & Footer (public)
  getNavigationFooter: () => api.get('/navigation'),
  getNavigation: () => api.get('/navigation/navigation'),
  getFooter: () => api.get('/navigation/footer'),

  // Pages (public)
  getPages: (params) => api.get('/pages', { params }),
  getPageBySlug: (slug) => api.get(`/pages/${slug}`),

  // Blog (public)
  getBlogPosts: (params) => api.get('/blog/posts', { params }),
  getBlogPost: (slug) => api.get(`/blog/posts/${slug}`),
  recordBlogPostView: (slug) => api.post(`/blog/posts/${slug}/view`),
  getBlogCategories: () => api.get('/blog/categories'),
  getBlogCategory: (slug) => api.get(`/blog/categories/${slug}`),
  getBlogTags: () => api.get('/blog/tags'),
  getBlogTag: (slug) => api.get(`/blog/tags/${slug}`),

  // TV Display (public)
  getTvChannel: (slug) => api.get(`/tv/channels/${slug}`),

  // Bulletin (public)
  getBulletin: () => api.get('/bulletin'),
  getBulletinByDate: (date) => api.get(`/bulletin/preview/${date}`),

  // Public Resources
  getPublicResources: (params) => api.get('/resources/documents', { params }),
  downloadPublicResource: (docId, password) => api.post(`/resources/documents/${docId}/download`, { password }, { responseType: 'blob', timeout: 60000 }),

  // Music (public)
  getMusicAlbums: (params) => api.get('/music/albums', { params }),
  getMusicAlbum: (slug) => api.get(`/music/albums/${slug}`),
  getMusicTracks: (params) => api.get('/music/tracks', { params }),
  getMusicTrack: (slug) => api.get(`/music/tracks/${slug}`),
  getMusicSongbook: (params) => api.get('/music/songbook', { params }),
  getMusicGenres: () => api.get('/music/genres'),
  getMusicArtists: () => api.get('/music/artists'),
  getMusicStreamUrl: (trackId) => `${API_BASE_URL}/music/stream/${trackId}`,
  getMusicRSSUrl: () => `${API_BASE_URL}/music/rss`,
  getPublicPlaylist: (id) => api.get(`/music/playlists/public/${id}`),

  // Podcast (public)
  getPodcastPage: () => api.get('/podcast/page'),
  getPodcastEpisodes: (count = 10) => api.get('/podcast/episodes', { params: { count } }),
  submitPodcastSuggestion: (data) => api.post('/podcast/suggest', data),

  // Bible (public)
  getBibleTranslations: () => api.get('/bible/translations'),
  getBibleBooks: () => api.get('/bible/books'),
  getBibleChapter: (abbreviation, chapter, translation) =>
    api.get(`/bible/books/${abbreviation}/chapters/${chapter}`, {
      params: translation ? { translation } : {},
    }),
  // Free-text reference lookup ("John 3:16-18"), used by the sermon page.
  getBiblePassage: (reference, translation) =>
    api.get('/bible/passage', { params: { reference, ...(translation ? { translation } : {}) } }),

  // Rooms (public)
  getRoomReservations: (date) => api.get('/rooms', { params: date ? { date } : {} }),

  // Forms (public)
  getPublicForm: (slug) => api.get(`/forms/${slug}`),
  submitForm: (slug, data) => api.post(`/forms/${slug}/submit`, data),

  // Alert Banners (public)
  getAlertBanners: () => api.get('/alert-banners'),

  // Page Hero Settings (public)
  getPageHeroSettings: () => api.get('/page-hero-settings'),
  getPageHeroSettingsByKey: (key) => api.get(`/page-hero-settings/${key}`),

  // Beliefs (public)
  getBeliefs: () => api.get('/beliefs'),

  // About page (public)
  getAboutPage: () => api.get('/about-page'),

  // Ministries page (public)
  getMinistries: () => api.get('/ministries'),

  // Fellowship (public)
  getFellowshipWeeks: (year) => api.get(`/fellowship/${year}`),
  getFellowshipTvData: () => api.get('/fellowship/tv/data'),
  submitFellowshipSignup: (data) => api.post('/fellowship/signup', data),

  // ============================================
  // Auth (Admin)
  // ============================================
  login: (data) => api.post('/auth/login', data),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  getCurrentUser: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get('/auth/verify-token', { params: { token } }),
  setPassword: (data) => api.post('/auth/set-password', data),
  logout: () => api.post('/auth/logout'),

  // ============================================
  // Music User Auth
  // ============================================
  musicAuth: {
    register: (data) => api.post('/music/auth/register', data),
    login: (data) => api.post('/music/auth/login', data),
    getCurrentUser: () => api.get('/music/auth/me'),
    updateProfile: (data) => api.put('/music/auth/profile', data),
    changePassword: (data) => api.put('/music/auth/password', data),
    forgotPassword: (email) => api.post('/music/auth/forgot-password', { email }),
    resetPassword: (data) => api.post('/music/auth/reset-password', data),
  },

  // ============================================
  // Music Playlists (requires music user auth)
  // ============================================
  musicPlaylists: {
    getAll: (params) => api.get('/music/playlists', { params }),
    get: (id) => api.get(`/music/playlists/${id}`),
    create: (data) => api.post('/music/playlists', data),
    update: (id, data) => api.put(`/music/playlists/${id}`, data),
    delete: (id) => api.delete(`/music/playlists/${id}`),
    addTrack: (playlistId, trackId) => api.post(`/music/playlists/${playlistId}/tracks`, { trackId }),
    removeTrack: (playlistId, trackId) => api.delete(`/music/playlists/${playlistId}/tracks/${trackId}`),
    reorderTracks: (playlistId, trackIds) => api.put(`/music/playlists/${playlistId}/reorder`, { trackIds }),
  },

  // ============================================
  // Member Auth
  // ============================================
  memberSignup: (data) => api.post('/members/auth/signup', data),
  memberLogin: (data) => api.post('/members/auth/login', data),
  memberVerifyEmail: (token) => api.get(`/members/auth/verify-email/${token}`),
  memberResendVerification: (email) => api.post('/members/auth/resend-verification', { email }),
  memberForgotPassword: (email) => api.post('/members/auth/forgot-password', { email }),
  memberResetPassword: (token, password) => api.post('/members/auth/reset-password', { token, password }),
  memberChangePassword: (currentPassword, newPassword) => api.put('/members/auth/password', { currentPassword, newPassword }),
  getMemberCurrent: () => api.get('/members/auth/me'),
  memberLogout: () => api.post('/members/auth/logout'),

  // ============================================
  // Member Profile (requires member auth)
  // ============================================
  getMemberProfile: () => api.get('/members/profile'),
  getMemberFamily: () => api.get('/members/profile/family'),
  getMemberRegistrations: (params) => api.get('/members/profile/registrations', { params }),
  getMemberPreferences: () => api.get('/members/profile/preferences'),
  updateMemberPreferences: (data) => api.put('/members/profile/preferences', data),
  getMemberGroups: () => api.get('/members/profile/groups'),

  // ============================================
  // Member Bible (requires member auth)
  // ============================================
  getMemberBibleSettings: (deviceId) => api.get('/members/bible/settings', { params: deviceId ? { deviceId } : {} }),
  updateMemberBibleSettings: (data) => api.put('/members/bible/settings', data),
  getMemberBiblePosition: (deviceId) => api.get('/members/bible/position', { params: deviceId ? { deviceId } : {} }),
  saveMemberBiblePosition: (data) => api.put('/members/bible/position', data),
  getMemberBibleAnnotations: (book, chapter) =>
    api.get('/members/bible/annotations', { params: { book, chapter } }),
  getMemberBibleBookmarks: () => api.get('/members/bible/bookmarks'),
  createMemberBibleBookmark: (data) => api.post('/members/bible/bookmarks', data),
  deleteMemberBibleBookmark: (id) => api.delete(`/members/bible/bookmarks/${id}`),
  getMemberBibleHighlights: () => api.get('/members/bible/highlights'),
  setMemberBibleHighlights: (data) => api.put('/members/bible/highlights', data),
  clearMemberBibleHighlights: (data) => api.post('/members/bible/highlights/clear', data),
  getMemberBibleNotes: (params) => api.get('/members/bible/notes', { params }),
  createMemberBibleNote: (data) => api.post('/members/bible/notes', data),
  updateMemberBibleNote: (id, data) => api.put(`/members/bible/notes/${id}`, data),
  deleteMemberBibleNote: (id) => api.delete(`/members/bible/notes/${id}`),

  // ============================================
  // Member Resources (requires member auth)
  // ============================================
  getMyDocuments: (params) => api.get('/members/resources/my-documents', { params }),
  downloadMyDocument: (docId, password) => api.post(`/members/resources/my-documents/${docId}/download`, { password }, { responseType: 'blob' }),
  getSharedDocuments: (params) => api.get('/members/resources/shared', { params }),
  downloadSharedDocument: (docId, password) => api.post(`/members/resources/shared/${docId}/download`, { password }, { responseType: 'blob' }),

  // ============================================
  // Small Groups (public + member auth)
  // ============================================
  getPublicSmallGroups: () => api.get('/small-groups'),
  getPublicSmallGroup: (id) => api.get(`/small-groups/${id}`),
  joinSmallGroup: (id) => api.post(`/small-groups/${id}/join`),
  leaveSmallGroup: (id) => api.delete(`/small-groups/${id}/leave`),
  getPublicShepherdGroups: () => api.get('/shepherd-groups'),
  getPublicShepherdGroup: (id) => api.get(`/shepherd-groups/${id}`),
  joinShepherdGroup: (id) => api.post(`/shepherd-groups/${id}/join`),
  leaveShepherdGroup: (id) => api.delete(`/shepherd-groups/${id}/leave`),

  // ============================================
  // Newsletter (public)
  // ============================================
  subscribeNewsletter: (data) => api.post('/newsletter/subscribe', data),
  getNewsletterTopics: () => api.get('/newsletter/topics'),
  getNewsletterPreferences: (token) => api.get(`/newsletter/preferences/${token}`),
  updateNewsletterPreferences: (token, data) => api.post(`/newsletter/preferences/${token}`, data),

  // ============================================
  // Admin API
  // ============================================
  admin: {
    // Dashboard
    getDashboardStats: () => api.get('/admin/dashboard/stats'),
    getTrafficStats: () => api.get('/admin/analytics/traffic'),
    
    // Users
    getUsers: (params) => api.get('/admin/users', { params }),
    getUser: (id) => api.get(`/admin/users/${id}`),
    createUser: (data) => api.post('/admin/users', data),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    deleteUser: (id) => api.delete(`/admin/users/${id}`),
    resendInvitation: (id, options = {}) => api.post(`/admin/users/${id}/resend-invitation`, options),

    // Profile
    getProfile: () => api.get('/admin/profile'),
    updateProfile: (data) => api.put('/admin/profile', data),
    changePassword: (data) => api.put('/admin/profile/password', data),
    getNotificationPreferences: () => api.get('/admin/profile/notification-preferences'),
    updateNotificationPreferences: (data) => api.put('/admin/profile/notification-preferences', data),
    
    // Settings
    getAllSettings: () => api.get('/admin/settings'),
    getSetting: (key) => api.get(`/admin/settings/${key}`),
    updateSetting: (key, data) => api.put(`/admin/settings/${key}`, data),
    bulkUpdateSettings: (settings) => api.put('/admin/settings', { settings }),
    deleteSetting: (key) => api.delete(`/admin/settings/${key}`),
    sendTestEmail: (email) => api.post('/admin/settings/test-email', { email }),
    
    // Announcements
    getAnnouncements: (params) => api.get('/admin/announcements', { params }),
    getAnnouncement: (id) => api.get(`/admin/announcements/${id}`),
    createAnnouncement: (data) => api.post('/admin/announcements', data),
    updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
    deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
    reorderAnnouncements: (order) => api.put('/admin/announcements/reorder', { order }),
    
    // Sermons
    getSermons: (params) => api.get('/admin/sermons', { params }),
    getSermon: (id) => api.get(`/admin/sermons/${id}`),
    createSermon: (data) => api.post('/admin/sermons', data),
    updateSermon: (id, data) => api.put(`/admin/sermons/${id}`, data),
    deleteSermon: (id) => api.delete(`/admin/sermons/${id}`),
    bulkAssignSermonSeries: (sermonIds, seriesId) => api.post('/admin/sermons/bulk-series', { sermonIds, seriesId }),
    
    // Sermon Series
    getSeries: () => api.get('/admin/sermon-series'),
    createSeries: (data) => api.post('/admin/sermon-series', data),
    updateSeries: (id, data) => api.put(`/admin/sermon-series/${id}`, data),
    deleteSeries: (id) => api.delete(`/admin/sermon-series/${id}`),

    // Sermon Transcriptions
    getTranscription: (sermonId) => api.get(`/admin/sermons/${sermonId}/transcription`),
    generateTranscription: (sermonId) => api.post(`/admin/sermons/${sermonId}/transcription/generate`),
    updateTranscription: (sermonId, data) => api.put(`/admin/sermons/${sermonId}/transcription`, data),
    toggleTranscription: (sermonId, enabled) => api.put(`/admin/sermons/${sermonId}/transcription/toggle`, { is_enabled: enabled }),
    deleteTranscription: (sermonId) => api.delete(`/admin/sermons/${sermonId}/transcription`),

    // Sermon RSS Feed
    getRSSSettings: () => api.get('/admin/rss-feed/settings'),
    updateRSSSettings: (data) => api.put('/admin/rss-feed/settings', data),
    triggerRSSSync: () => api.post('/admin/rss-feed/sync'),
    getRSSSyncStatus: () => api.get('/admin/rss-feed/status'),

    // Podcast Page
    getPodcastPage: () => api.get('/admin/podcast/page'),
    updatePodcastPage: (data) => api.put('/admin/podcast/page', data),

    // Podcast Episode RSS Feed
    getPodcastRSSSettings: () => api.get('/admin/podcast/rss'),
    updatePodcastRSSSettings: (data) => api.put('/admin/podcast/rss', data),
    triggerPodcastRSSSync: () => api.post('/admin/podcast/rss/sync'),
    getPodcastRSSSyncStatus: () => api.get('/admin/podcast/rss/status'),

    // Events
    getEvents: (params) => api.get('/admin/events', { params }),
    getEvent: (id) => api.get(`/admin/events/${id}`),
    createEvent: (data) => api.post('/admin/events', data),
    updateEvent: (id, data) => api.put(`/admin/events/${id}`, data),
    deleteEvent: (id) => api.delete(`/admin/events/${id}`),
    getEventRegistrations: (eventId, params) => api.get(`/admin/events/${eventId}/registrations`, { params }),
    cancelEventRegistration: (eventId, registrationId) => api.delete(`/admin/events/${eventId}/registrations/${registrationId}`),
    getEventResources: (eventId) => api.get(`/admin/events/${eventId}/resources`),
    setEventResources: (eventId, resourceIds) => api.put(`/admin/events/${eventId}/resources`, { resourceIds }),
    
    // Contacts
    getContacts: (params) => api.get('/admin/contacts', { params }),
    getContact: (id) => api.get(`/admin/contacts/${id}`),
    updateContact: (id, data) => api.put(`/admin/contacts/${id}`, data),
    deleteContact: (id) => api.delete(`/admin/contacts/${id}`),
    
    // Small Groups
    getSmallGroupStats: () => api.get('/admin/small-groups/stats'),
    getSmallGroups: (params) => api.get('/admin/small-groups', { params }),
    getSmallGroup: (id) => api.get(`/admin/small-groups/${id}`),
    createSmallGroup: (data) => api.post('/admin/small-groups', data),
    updateSmallGroup: (id, data) => api.put(`/admin/small-groups/${id}`, data),
    deleteSmallGroup: (id) => api.delete(`/admin/small-groups/${id}`),

    // Shepherd Groups
    getShepherdGroupStats: () => api.get('/admin/shepherd-groups/stats'),
    getShepherdGroups: (params) => api.get('/admin/shepherd-groups', { params }),
    getShepherdGroup: (id) => api.get(`/admin/shepherd-groups/${id}`),
    createShepherdGroup: (data) => api.post('/admin/shepherd-groups', data),
    updateShepherdGroup: (id, data) => api.put(`/admin/shepherd-groups/${id}`, data),
    deleteShepherdGroup: (id) => api.delete(`/admin/shepherd-groups/${id}`),

    // Leadership - Types
    getLeaderTypes: (params) => api.get('/admin/leadership/types', { params }),
    createLeaderType: (data) => api.post('/admin/leadership/types', data),
    updateLeaderType: (id, data) => api.put(`/admin/leadership/types/${id}`, data),
    deleteLeaderType: (id) => api.delete(`/admin/leadership/types/${id}`),
    reorderLeaderTypes: (order) => api.put('/admin/leadership/types/reorder', { order }),

    // Leadership - Leaders
    getLeaders: (params) => api.get('/admin/leadership/leaders', { params }),
    getLeader: (id) => api.get(`/admin/leadership/leaders/${id}`),
    createLeader: (data) => api.post('/admin/leadership/leaders', data),
    updateLeader: (id, data) => api.put(`/admin/leadership/leaders/${id}`, data),
    deleteLeader: (id) => api.delete(`/admin/leadership/leaders/${id}`),

    // Leadership - Sections
    getLeadershipSections: (params) => api.get('/admin/leadership/sections', { params }),
    getLeadershipSection: (id) => api.get(`/admin/leadership/sections/${id}`),
    createLeadershipSection: (data) => api.post('/admin/leadership/sections', data),
    updateLeadershipSection: (id, data) => api.put(`/admin/leadership/sections/${id}`, data),
    deleteLeadershipSection: (id) => api.delete(`/admin/leadership/sections/${id}`),
    reorderLeadershipSections: (order) => api.put('/admin/leadership/sections/reorder', { order }),
    getSectionLeaders: (id) => api.get(`/admin/leadership/sections/${id}/leaders`),
    updateSectionLeaders: (id, leaderIds) => api.put(`/admin/leadership/sections/${id}/leaders`, { leaderIds }),

    // Media
    getMediaFiles: (params) => api.get('/admin/media', { params }),
    uploadMedia: (formData) => api.post('/admin/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    publishMedia: (id) => api.post(`/admin/media/${id}/publish`),
    unpublishMedia: (id) => api.post(`/admin/media/${id}/unpublish`),
    bulkPublishMedia: (ids) => api.post('/admin/media/bulk-publish', { ids }),
    deleteMedia: (id) => api.delete(`/admin/media/${id}`),

    // Pages
    getPages: (params) => api.get('/admin/pages', { params }),
    getPage: (id) => api.get(`/admin/pages/${id}`),
    createPage: (data) => api.post('/admin/pages', data),
    updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
    deletePage: (id) => api.delete(`/admin/pages/${id}`),
    getPageChildren: (id) => api.get(`/admin/pages/${id}/children`),

    // Forms
    getForms: (params) => api.get('/admin/forms', { params }),
    getForm: (id) => api.get(`/admin/forms/${id}`),
    createForm: (data) => api.post('/admin/forms', data),
    updateForm: (id, data) => api.put(`/admin/forms/${id}`, data),
    deleteForm: (id) => api.delete(`/admin/forms/${id}`),
    duplicateForm: (id) => api.post(`/admin/forms/${id}/duplicate`),

    // Form Fields
    getFormFields: (formId) => api.get(`/admin/forms/${formId}/fields`),
    createFormField: (formId, data) => api.post(`/admin/forms/${formId}/fields`, data),
    updateFormField: (formId, fieldId, data) => api.put(`/admin/forms/${formId}/fields/${fieldId}`, data),
    deleteFormField: (formId, fieldId) => api.delete(`/admin/forms/${formId}/fields/${fieldId}`),
    reorderFormFields: (formId, order) => api.put(`/admin/forms/${formId}/fields/reorder`, { order }),
    updateFormFieldsLayout: (formId, layout) => api.put(`/admin/forms/${formId}/fields/layout`, { layout }),

    // Form Field Rules
    getFieldRules: (formId, fieldId) => api.get(`/admin/forms/${formId}/fields/${fieldId}/rules`),
    createFieldRule: (formId, fieldId, data) => api.post(`/admin/forms/${formId}/fields/${fieldId}/rules`, data),
    updateFieldRule: (formId, ruleId, data) => api.put(`/admin/forms/${formId}/rules/${ruleId}`, data),
    deleteFieldRule: (formId, ruleId) => api.delete(`/admin/forms/${formId}/rules/${ruleId}`),

    // Form Submissions
    getFormSubmissions: (formId, params) => api.get(`/admin/forms/${formId}/submissions`, { params }),
    getFormSubmission: (formId, submissionId) => api.get(`/admin/forms/${formId}/submissions/${submissionId}`),
    updateFormSubmission: (formId, submissionId, data) => api.put(`/admin/forms/${formId}/submissions/${submissionId}`, data),
    deleteFormSubmission: (formId, submissionId) => api.delete(`/admin/forms/${formId}/submissions/${submissionId}`),
    exportFormSubmissions: (formId, format = 'csv') => api.get(`/admin/forms/${formId}/submissions/export`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),

    // Blog Posts
    getBlogPosts: (params) => api.get('/admin/blog/posts', { params }),
    getBlogPost: (id) => api.get(`/admin/blog/posts/${id}`),
    createBlogPost: (data) => api.post('/admin/blog/posts', data),
    updateBlogPost: (id, data) => api.put(`/admin/blog/posts/${id}`, data),
    deleteBlogPost: (id) => api.delete(`/admin/blog/posts/${id}`),

    // Blog Categories
    getBlogCategories: () => api.get('/admin/blog/categories'),
    createBlogCategory: (data) => api.post('/admin/blog/categories', data),
    updateBlogCategory: (id, data) => api.put(`/admin/blog/categories/${id}`, data),
    deleteBlogCategory: (id) => api.delete(`/admin/blog/categories/${id}`),

    // Blog Tags
    getBlogTags: () => api.get('/admin/blog/tags'),
    createBlogTag: (data) => api.post('/admin/blog/tags', data),
    updateBlogTag: (id, data) => api.put(`/admin/blog/tags/${id}`, data),
    deleteBlogTag: (id) => api.delete(`/admin/blog/tags/${id}`),

    // Catechism
    getCatechism: (params) => api.get('/admin/catechism', { params }),
    getCatechismQuestion: (id) => api.get(`/admin/catechism/${id}`),
    updateCatechism: (id, data) => api.put(`/admin/catechism/${id}`, data),
    getWeeklyFeatured: () => api.get('/admin/catechism/weekly/featured'),
    setWeeklyFeatured: (ids) => api.put('/admin/catechism/weekly/featured', { ids }),
    toggleWeeklyFeatured: (id) => api.post(`/admin/catechism/${id}/toggle-featured`),

    // Homepage - Hero Slides
    getHeroSlides: () => api.get('/admin/homepage/hero-slides'),
    createHeroSlide: (data) => api.post('/admin/homepage/hero-slides', data),
    updateHeroSlide: (id, data) => api.put(`/admin/homepage/hero-slides/${id}`, data),
    deleteHeroSlide: (id) => api.delete(`/admin/homepage/hero-slides/${id}`),

    getServiceTimes: () => api.get('/admin/homepage/service-times'),
    createServiceTime: (data) => api.post('/admin/homepage/service-times', data),
    updateServiceTime: (id, data) => api.put(`/admin/homepage/service-times/${id}`, data),
    deleteServiceTime: (id) => api.delete(`/admin/homepage/service-times/${id}`),

    getLocation: () => api.get('/admin/homepage/location'),
    updateLocation: (data) => api.put('/admin/homepage/location', data),

    getConnectCards: () => api.get('/admin/homepage/connect-cards'),
    createConnectCard: (data) => api.post('/admin/homepage/connect-cards', data),
    updateConnectCard: (id, data) => api.put(`/admin/homepage/connect-cards/${id}`, data),
    deleteConnectCard: (id) => api.delete(`/admin/homepage/connect-cards/${id}`),

    // Navigation
    getNavigation: () => api.get('/admin/navigation'),
    createNavigationItem: (data) => api.post('/admin/navigation', data),
    updateNavigationItem: (id, data) => api.put(`/admin/navigation/${id}`, data),
    deleteNavigationItem: (id) => api.delete(`/admin/navigation/${id}`),

    // Footer Columns
    getFooterColumns: () => api.get('/admin/footer/columns'),
    createFooterColumn: (data) => api.post('/admin/footer/columns', data),
    updateFooterColumn: (id, data) => api.put(`/admin/footer/columns/${id}`, data),
    deleteFooterColumn: (id) => api.delete(`/admin/footer/columns/${id}`),

    // Footer Links
    createFooterLink: (data) => api.post('/admin/footer/links', data),
    updateFooterLink: (id, data) => api.put(`/admin/footer/links/${id}`, data),
    deleteFooterLink: (id) => api.delete(`/admin/footer/links/${id}`),

    // Social Media
    getSocialMedia: () => api.get('/admin/social-media'),
    createSocialMediaLink: (data) => api.post('/admin/social-media', data),
    updateSocialMediaLink: (id, data) => api.put(`/admin/social-media/${id}`, data),
    deleteSocialMediaLink: (id) => api.delete(`/admin/social-media/${id}`),

    // Footer Settings
    getFooterSettings: () => api.get('/admin/footer/settings'),
    updateFooterSettings: (data) => api.put('/admin/footer/settings', data),

    // Navigation Settings
    getNavigationSettings: () => api.get('/admin/navigation/settings'),
    updateNavigationSettings: (data) => api.put('/admin/navigation/settings', data),

    // TV Channels
    getTvChannels: (params) => api.get('/admin/tv/channels', { params }),
    getTvChannel: (id) => api.get(`/admin/tv/channels/${id}`),
    createTvChannel: (data) => api.post('/admin/tv/channels', data),
    updateTvChannel: (id, data) => api.put(`/admin/tv/channels/${id}`, data),
    deleteTvChannel: (id) => api.delete(`/admin/tv/channels/${id}`),
    getTvChannelSlides: (id) => api.get(`/admin/tv/channels/${id}/slides`),
    updateTvChannelSlides: (id, slideIds) => api.put(`/admin/tv/channels/${id}/slides`, { slideIds }),

    // TV Slides
    getTvSlides: (params) => api.get('/admin/tv/slides', { params }),
    getTvSlidesActive: () => api.get('/admin/tv/slides/active'),
    getTvSlide: (id) => api.get(`/admin/tv/slides/${id}`),
    createTvSlide: (data) => api.post('/admin/tv/slides', data),
    updateTvSlide: (id, data) => api.put(`/admin/tv/slides/${id}`, data),
    deleteTvSlide: (id) => api.delete(`/admin/tv/slides/${id}`),

    // Bulletin
    getBulletinSettings: () => api.get('/admin/bulletin/settings'),
    updateBulletinSettings: (data) => api.put('/admin/bulletin/settings', data),
    testBulletinConnection: (data) => api.post('/admin/bulletin/test-connection', data),
    triggerBulletinSync: () => api.post('/admin/bulletin/sync'),
    getBulletins: (params) => api.get('/admin/bulletin', { params }),
    getBulletin: (id) => api.get(`/admin/bulletin/${id}`),
    updateBulletin: (id, data) => api.put(`/admin/bulletin/${id}`, data),
    deleteBulletin: (id) => api.delete(`/admin/bulletin/${id}`),
    setBulletinCurrent: (id) => api.post(`/admin/bulletin/${id}/set-current`),

    // Music
    getMusicStats: () => api.get('/admin/music/stats'),
    // Music Albums
    getMusicAlbums: (params) => api.get('/admin/music/albums', { params }),
    getMusicAlbum: (id) => api.get(`/admin/music/albums/${id}`),
    createMusicAlbum: (data) => api.post('/admin/music/albums', data),
    updateMusicAlbum: (id, data) => api.put(`/admin/music/albums/${id}`, data),
    deleteMusicAlbum: (id) => api.delete(`/admin/music/albums/${id}`),
    reorderMusicTracks: (albumId, trackIds) => api.put(`/admin/music/albums/${albumId}/reorder`, { trackIds }),

    // Music Tracks
    getMusicTracks: (params) => api.get('/admin/music/tracks', { params }),
    getMusicTrack: (id) => api.get(`/admin/music/tracks/${id}`),
    createMusicTrack: (data) => api.post('/admin/music/tracks', data),
    updateMusicTrack: (id, data) => api.put(`/admin/music/tracks/${id}`, data),
    deleteMusicTrack: (id) => api.delete(`/admin/music/tracks/${id}`),
    uploadMusicTrack: (formData) => api.post('/admin/music/tracks/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minute timeout for large files
    }),

    // Music preview stream (no play count)
    getMusicPreviewUrl: (trackId) => `${API_BASE_URL}/admin/music/stream/${trackId}`,

    // Music RSS Settings
    getMusicRSSSettings: () => api.get('/admin/music/rss/settings'),
    updateMusicRSSSettings: (data) => api.put('/admin/music/rss/settings', data),

    // Rooms
    triggerRoomSync: () => api.post('/admin/rooms/sync'),

    // Cron Jobs
    getCronJobs: () => api.get('/admin/cron/jobs'),
    getCronJob: (id) => api.get(`/admin/cron/jobs/${id}`),
    updateCronJob: (id, data) => api.put(`/admin/cron/jobs/${id}`, data),
    triggerCronJob: (id) => api.post(`/admin/cron/jobs/${id}/trigger`),
    getCronJobLogs: (id, params) => api.get(`/admin/cron/jobs/${id}/logs`, { params }),
    getCronLogs: (params) => api.get('/admin/cron/logs', { params }),
    getCronPresets: () => api.get('/admin/cron/presets'),
    validateCronExpression: (expression) => api.post('/admin/cron/validate', { expression }),

    // Fellowship Food Signup
    getFellowshipWeeks: (year) => api.get('/admin/fellowship/weeks', { params: { year } }),
    generateFellowshipYear: (year) => api.post('/admin/fellowship/generate', { year }),
    getFellowshipSignups: (params) => api.get('/admin/fellowship/signups', { params }),
    approveFellowshipSignup: (id) => api.post(`/admin/fellowship/signups/${id}/approve`),
    rejectFellowshipSignup: (id, notes, skipEmail) => api.post(`/admin/fellowship/signups/${id}/reject`, { notes, skipEmail }),
    updateFellowshipWeek: (id, data) => api.put(`/admin/fellowship/weeks/${id}`, data),
    assignFellowshipWeek: (id, data) => api.post(`/admin/fellowship/weeks/${id}/assign`, data),
    clearFellowshipAssignment: (id) => api.delete(`/admin/fellowship/weeks/${id}/assign`),

    // Members
    getMembersSettings: () => api.get('/admin/members/settings'),
    updateMembersSettings: (data) => api.put('/admin/members/settings', data),
    testMembersConnection: () => api.post('/admin/members/test-connection'),
    triggerMembersSync: () => api.post('/admin/members/sync'),
    getMembers: (params) => api.get('/admin/members', { params }),
    getMember: (id) => api.get(`/admin/members/${id}`),
    createMember: (data) => api.post('/admin/members', data),
    updateMember: (id, data) => api.put(`/admin/members/${id}`, data),
    deleteMember: (id) => api.delete(`/admin/members/${id}`),
    getMemberRegistrations: (id, params) => api.get(`/admin/members/${id}/registrations`, { params }),
    adminResendMemberVerification: (id) => api.post(`/admin/members/${id}/resend-verification`),

    // Resource Categories
    getResourceCategories: () => api.get('/admin/resources/categories'),
    createResourceCategory: (data) => api.post('/admin/resources/categories', data),
    updateResourceCategory: (id, data) => api.put(`/admin/resources/categories/${id}`, data),
    deleteResourceCategory: (id) => api.delete(`/admin/resources/categories/${id}`),

    // Resource Documents (shared member resources)
    getResourceDocuments: (params) => api.get('/admin/resources/documents', { params }),
    getResourceDocument: (id) => api.get(`/admin/resources/documents/${id}`),
    uploadResourceDocument: (formData) => api.post('/admin/resources/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }),
    updateResourceDocument: (id, data) => api.put(`/admin/resources/documents/${id}`, data),
    deleteResourceDocument: (id) => api.delete(`/admin/resources/documents/${id}`),
    toggleResourceDocumentActive: (id) => api.patch(`/admin/resources/documents/${id}/toggle-active`),
    updateResourceDocumentPassword: (id, data) => api.put(`/admin/resources/documents/${id}/password`, data),
    downloadResourceDocumentAdmin: (id, password) => api.post(`/admin/resources/documents/${id}/download`, { password }, { responseType: 'blob' }),

    // Member-specific Documents (admin manages)
    getMemberDocuments: (memberId) => api.get(`/admin/resources/member/${memberId}/documents`),
    uploadMemberDocument: (memberId, formData) => api.post(`/admin/resources/member/${memberId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }),
    updateMemberDocument: (docId, data) => api.put(`/admin/resources/member-documents/${docId}`, data),
    deleteMemberDocument: (docId) => api.delete(`/admin/resources/member-documents/${docId}`),
    updateMemberDocumentPassword: (docId, data) => api.put(`/admin/resources/member-documents/${docId}/password`, data),

    // Small Group Members
    addSmallGroupMember: (groupId, data) => api.post(`/admin/small-groups/${groupId}/members`, data),
    removeSmallGroupMember: (groupId, membershipId) => api.delete(`/admin/small-groups/${groupId}/members/${membershipId}`),
    getSmallGroupMembers: (groupId, params) => api.get(`/admin/small-groups/${groupId}/members`, { params }),
    getPendingSmallGroupSignups: (params) => api.get('/admin/small-groups/signups/pending', { params }),
    approveSmallGroupSignup: (membershipId) => api.post(`/admin/small-groups/signups/${membershipId}/approve`),
    rejectSmallGroupSignup: (membershipId) => api.post(`/admin/small-groups/signups/${membershipId}/reject`),
    exportSmallGroupsExcel: () => api.get('/admin/small-groups/export/xlsx', { responseType: 'blob' }),
    searchMembersForGroup: (q) => api.get('/admin/small-groups/members/search', { params: { q } }),
    searchGroupsByMember: (q) => api.get('/admin/small-groups/search-by-member', { params: { q } }),

    // Shepherd Group Members
    addShepherdGroupMember: (groupId, data) => api.post(`/admin/shepherd-groups/${groupId}/members`, data),
    removeShepherdGroupMember: (groupId, membershipId) => api.delete(`/admin/shepherd-groups/${groupId}/members/${membershipId}`),
    getShepherdGroupMembers: (groupId, params) => api.get(`/admin/shepherd-groups/${groupId}/members`, { params }),
    getShepherdGroupMemberLocations: (groupId) => api.get(`/admin/shepherd-groups/${groupId}/member-locations`),
    getPendingShepherdGroupSignups: (params) => api.get('/admin/shepherd-groups/signups/pending', { params }),
    approveShepherdGroupSignup: (membershipId) => api.post(`/admin/shepherd-groups/signups/${membershipId}/approve`),
    rejectShepherdGroupSignup: (membershipId) => api.post(`/admin/shepherd-groups/signups/${membershipId}/reject`),
    exportShepherdGroupsExcel: () => api.get('/admin/shepherd-groups/export/xlsx', { responseType: 'blob' }),
    searchMembersForShepherdGroup: (q) => api.get('/admin/shepherd-groups/members/search', { params: { q } }),
    searchShepherdGroupsByMember: (q) => api.get('/admin/shepherd-groups/search-by-member', { params: { q } }),

    // Alert Banners
    getAlertBanners: (params) => api.get('/admin/alert-banners', { params }),
    getAlertBanner: (id) => api.get(`/admin/alert-banners/${id}`),
    createAlertBanner: (data) => api.post('/admin/alert-banners', data),
    updateAlertBanner: (id, data) => api.put(`/admin/alert-banners/${id}`, data),
    deleteAlertBanner: (id) => api.delete(`/admin/alert-banners/${id}`),
    reorderAlertBanners: (order) => api.put('/admin/alert-banners/reorder', { order }),
    getAlertBannerSettings: () => api.get('/admin/alert-banners/settings'),
    updateAlertBannerSettings: (data) => api.put('/admin/alert-banners/settings', data),

    // Redirects
    getRedirects: (params) => api.get('/admin/redirects', { params }),
    getRedirect: (id) => api.get(`/admin/redirects/${id}`),
    createRedirect: (data) => api.post('/admin/redirects', data),
    updateRedirect: (id, data) => api.put(`/admin/redirects/${id}`, data),
    deleteRedirect: (id) => api.delete(`/admin/redirects/${id}`),
    toggleRedirect: (id) => api.post(`/admin/redirects/${id}/toggle`),

    // Notifications
    getNotifications: (params) => api.get('/admin/notifications', { params }),
    markNotificationRead: (id) => api.post(`/admin/notifications/${id}/read`),
    markAllNotificationsRead: () => api.post('/admin/notifications/read-all'),

    // Newsletter
    getNewsletters: (params) => api.get('/admin/newsletters', { params }),
    getNewsletterStats: () => api.get('/admin/newsletters/stats'),
    getNewsletter: (id) => api.get(`/admin/newsletters/${id}`),
    createNewsletter: (data) => api.post('/admin/newsletters', data),
    updateNewsletter: (id, data) => api.put(`/admin/newsletters/${id}`, data),
    deleteNewsletter: (id) => api.delete(`/admin/newsletters/${id}`),
    sendNewsletter: (id) => api.post(`/admin/newsletters/${id}/send`),
    scheduleNewsletter: (id, data) => api.post(`/admin/newsletters/${id}/schedule`, data),
    cancelScheduledNewsletter: (id) => api.post(`/admin/newsletters/${id}/cancel-schedule`),
    duplicateNewsletter: (id) => api.post(`/admin/newsletters/${id}/duplicate`),
    previewNewsletter: (id) => api.get(`/admin/newsletters/${id}/preview`),
    sendTestNewsletter: (id, data) => api.post(`/admin/newsletters/${id}/test-email`, data),
    getNewsletterAnalytics: (id) => api.get(`/admin/newsletters/${id}/analytics`),
    createABTest: (id, data) => api.post(`/admin/newsletters/${id}/ab-test`, data),
    pickABWinner: (id) => api.post(`/admin/newsletters/${id}/ab-winner`),

    // Newsletter Subscribers
    getNewsletterSubscribers: (params) => api.get('/admin/newsletter-subscribers', { params }),
    getNewsletterSubscriber: (id) => api.get(`/admin/newsletter-subscribers/${id}`),
    createNewsletterSubscriber: (data) => api.post('/admin/newsletter-subscribers', data),
    updateNewsletterSubscriber: (id, data) => api.put(`/admin/newsletter-subscribers/${id}`, data),
    deleteNewsletterSubscriber: (id) => api.delete(`/admin/newsletter-subscribers/${id}`),
    importNewsletterSubscribers: (data) => api.post('/admin/newsletter-subscribers/import', data),
    exportNewsletterSubscribers: () => api.get('/admin/newsletter-subscribers/export', { responseType: 'blob' }),

    // Newsletter Templates
    getNewsletterTemplates: () => api.get('/admin/newsletter-templates'),
    getNewsletterTemplate: (id) => api.get(`/admin/newsletter-templates/${id}`),
    createNewsletterTemplate: (data) => api.post('/admin/newsletter-templates', data),
    updateNewsletterTemplate: (id, data) => api.put(`/admin/newsletter-templates/${id}`, data),
    deleteNewsletterTemplate: (id) => api.delete(`/admin/newsletter-templates/${id}`),

    // Newsletter Segments
    getNewsletterSegments: () => api.get('/admin/newsletter-segments'),
    createNewsletterSegment: (data) => api.post('/admin/newsletter-segments', data),
    updateNewsletterSegment: (id, data) => api.put(`/admin/newsletter-segments/${id}`, data),
    deleteNewsletterSegment: (id) => api.delete(`/admin/newsletter-segments/${id}`),
    previewNewsletterSegment: (id) => api.get(`/admin/newsletter-segments/${id}/preview`),

    // Newsletter Topics
    getNewsletterTopics: () => api.get('/admin/newsletter-topics'),
    createNewsletterTopic: (data) => api.post('/admin/newsletter-topics', data),
    updateNewsletterTopic: (id, data) => api.put(`/admin/newsletter-topics/${id}`, data),
    deleteNewsletterTopic: (id) => api.delete(`/admin/newsletter-topics/${id}`),

    // Newsletter Automations
    getNewsletterAutomations: () => api.get('/admin/newsletter-automations'),
    createNewsletterAutomation: (data) => api.post('/admin/newsletter-automations', data),
    updateNewsletterAutomation: (id, data) => api.put(`/admin/newsletter-automations/${id}`, data),
    deleteNewsletterAutomation: (id) => api.delete(`/admin/newsletter-automations/${id}`),
    toggleNewsletterAutomation: (id) => api.post(`/admin/newsletter-automations/${id}/toggle`),

    // Newsletter RSS
    getNewsletterRSSSettings: () => api.get('/admin/newsletter-rss/settings'),
    updateNewsletterRSSSettings: (data) => api.put('/admin/newsletter-rss/settings', data),
    generateNewsletterRSS: () => api.post('/admin/newsletter-rss/generate'),

    // Page Hero Settings
    getPageHeroSettings: () => api.get('/admin/page-hero-settings'),
    updatePageHeroSettings: (pageKey, data) => api.put(`/admin/page-hero-settings/${pageKey}`, data),

    // Beliefs
    getBeliefs: () => api.get('/admin/beliefs'),
    updateBeliefsContent: (key, data) => api.put(`/admin/beliefs/content/${key}`, data),
    createBeliefsDoctrine: (data) => api.post('/admin/beliefs/doctrines', data),
    updateBeliefsDoctrine: (id, data) => api.put(`/admin/beliefs/doctrines/${id}`, data),
    deleteBeliefsDoctrine: (id) => api.delete(`/admin/beliefs/doctrines/${id}`),
    reorderBeliefsDoctrines: (order) => api.put('/admin/beliefs/doctrines/reorder', { order }),
    createBeliefsConfessionalDoc: (data) => api.post('/admin/beliefs/confessional-docs', data),
    updateBeliefsConfessionalDoc: (id, data) => api.put(`/admin/beliefs/confessional-docs/${id}`, data),
    deleteBeliefsConfessionalDoc: (id) => api.delete(`/admin/beliefs/confessional-docs/${id}`),
    createBeliefsStatement: (data) => api.post('/admin/beliefs/statements', data),
    updateBeliefsStatement: (id, data) => api.put(`/admin/beliefs/statements/${id}`, data),
    deleteBeliefsStatement: (id) => api.delete(`/admin/beliefs/statements/${id}`),

    // About page
    getAboutPage: () => api.get('/admin/about'),
    updateAboutContent: (key, data) => api.put(`/admin/about/content/${key}`, data),
    createAboutValue: (data) => api.post('/admin/about/values', data),
    updateAboutValue: (id, data) => api.put(`/admin/about/values/${id}`, data),
    deleteAboutValue: (id) => api.delete(`/admin/about/values/${id}`),
    reorderAboutValues: (order) => api.put('/admin/about/values/reorder', { order }),

    // Ministries page
    getMinistries: () => api.get('/admin/ministries'),
    updateMinistriesContent: (key, data) => api.put(`/admin/ministries/content/${key}`, data),
    createMinistryItem: (data) => api.post('/admin/ministries/items', data),
    updateMinistryItem: (id, data) => api.put(`/admin/ministries/items/${id}`, data),
    deleteMinistryItem: (id) => api.delete(`/admin/ministries/items/${id}`),
    reorderMinistryItems: (order) => api.put('/admin/ministries/items/reorder', { order }),
  },
};

export default api;
